import * as Phaser from 'phaser';
import { GAME_EVENTS, SCENES, type GameOverPayload } from '@/types';
import { GAME_HEIGHT, GAME_WIDTH } from '@/config/gameConfig';
import { BALANCE } from '@/config/balance';
import { TEXTURE_KEYS } from '@/utils/textures';
import { Elephant } from '@/entities/Elephant';
import { Nutrient } from '@/entities/Nutrient';
import { Monster } from '@/entities/Monster';
import { Shield } from '@/entities/Shield';
import { InputSystem } from '@/systems/InputSystem';
import { ScoreSystem } from '@/systems/ScoreSystem';
import { SpawnSystem } from '@/systems/SpawnSystem';
import { NutrientTooltip } from '@/ui/NutrientTooltip';
import { emitBurst } from '@/ui/particles';
import { ObjectPool } from '@/utils/pool';
import { SeededRNG, generateSeed } from '@/utils/rng';
import { NUTRIENTS, isDielacNutrient } from '@/config/nutrients';
import type { Lane, MonsterHitPayload, NutrientCollectedPayload, StageChangedPayload } from '@/types';

type GameState = 'countdown' | 'playing' | 'paused' | 'over';

type SpawnPhase = (typeof BALANCE.spawn.phases)[number];

export class GameScene extends Phaser.Scene {
  private state: GameState = 'countdown';
  private elapsedMs = 0;
  private spawnAccumulator = 0;

  private bgTile?: Phaser.GameObjects.TileSprite;
  private elephant?: Elephant;
  private inputSystem?: InputSystem;
  private nutrientPool?: ObjectPool<Nutrient>;
  private monsterPool?: ObjectPool<Monster>;
  private shieldPool?: ObjectPool<Shield>;
  private spawnSystem?: SpawnSystem;
  private scoreSystem = new ScoreSystem();
  private shieldNextSpawnMs = 0;
  private shieldExpireMs = 0;
  private firstShieldScheduled = false;
  private shieldRing?: Phaser.GameObjects.Arc;
  private shieldPulseTween?: Phaser.Tweens.Tween;
  private shieldWarning = false;
  private rng?: SeededRNG;
  private countdownText?: Phaser.GameObjects.Text;
  private pauseLayer?: Phaser.GameObjects.Container;

  constructor() {
    super(SCENES.Game);
  }

  create(): void {
    this.state = 'countdown';
    this.elapsedMs = 0;
    this.spawnAccumulator = 0;

    this.cameras.main.setBackgroundColor('#87ceeb');

    this.rng = new SeededRNG(generateSeed());
    this.nutrientPool = new ObjectPool(() => new Nutrient(this));
    this.monsterPool = new ObjectPool(() => new Monster(this));
    this.shieldPool = new ObjectPool(() => new Shield(this));
    this.spawnSystem = new SpawnSystem(this, this.rng, {
      acquireNutrient: () => this.nutrientPool!.acquire(),
      acquireMonster: () => this.monsterPool!.acquire(),
    });
    new NutrientTooltip(this); // self-subscribes; auto cleanup on shutdown
    this.scoreSystem.reset();
    this.shieldExpireMs = 0;
    this.shieldWarning = false;
    this.firstShieldScheduled = false;

    this.drawBackground();
    this.createElephant();

    this.scene.launch(SCENES.HUD);

    this.inputSystem = new InputSystem(this, (dir) => this.elephant?.shiftLane(dir));

    this.events.on(GAME_EVENTS.StageChanged, this.onStageChanged, this);

    this.bindBlurFocus();
    this.startCountdown();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scene.stop(SCENES.HUD);
      this.events.off(GAME_EVENTS.StageChanged, this.onStageChanged, this);
      this.cleanup();
    });
  }

  getElapsedMs(): number {
    return this.elapsedMs;
  }

  getShieldRemainingMs(): number {
    if (!this.elephant?.isInvincible()) return 0;
    return Math.max(0, this.shieldExpireMs - this.elapsedMs);
  }

  override update(_time: number, delta: number): void {
    if (this.state !== 'playing') return;
    this.elapsedMs += delta;
    const phase = this.getCurrentPhase();
    if (phase) {
      this.scrollBackground(phase, delta);
      this.tickSpawn(phase, delta);
    }
    this.tickShieldSpawn();
    this.tickShieldExpiry();
    this.checkCollisions();
    this.despawnOutOfBounds();
    if (this.elapsedMs >= BALANCE.game.durationMs) {
      this.endGame('timer');
    }
  }

  private drawBackground(): void {
    this.bgTile = this.add
      .tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, TEXTURE_KEYS.background)
      .setOrigin(0, 0);
  }

  private createElephant(): void {
    const startX = BALANCE.lanes.positions[1];
    this.elephant = new Elephant(this, startX, BALANCE.game.elephantY);
    this.elephant.setDepth(20);
  }

  private getCurrentPhase(): SpawnPhase | undefined {
    return BALANCE.spawn.phases.find(
      (p) => this.elapsedMs >= p.startMs && this.elapsedMs < p.endMs,
    );
  }

  private scrollBackground(phase: SpawnPhase, delta: number): void {
    if (!this.bgTile) return;
    this.bgTile.tilePositionY -= (phase.scrollSpeed * delta) / 1000;
  }

  private tickSpawn(phase: SpawnPhase, delta: number): void {
    this.spawnAccumulator += delta;
    if (this.spawnAccumulator >= phase.intervalMs) {
      this.spawnAccumulator -= phase.intervalMs;
      this.spawnSystem?.tick(phase.difficulties, phase.scrollSpeed);
    }
  }

  private despawnOutOfBounds(): void {
    this.nutrientPool?.forEachActive((n) => {
      if (n.isOutOfBounds()) this.nutrientPool!.release(n);
    });
    this.monsterPool?.forEachActive((m) => {
      if (m.isOutOfBounds()) this.monsterPool!.release(m);
    });
    this.shieldPool?.forEachActive((s) => {
      if (s.isOutOfBounds()) this.shieldPool!.release(s);
    });
  }

  private checkCollisions(): void {
    if (!this.elephant) return;
    const elephant = this.elephant;
    this.nutrientPool?.forEachActive((nutrient) => {
      if (this.physics.overlap(elephant, nutrient)) this.handleCollect(nutrient);
    });
    this.monsterPool?.forEachActive((monster) => {
      if (this.physics.overlap(elephant, monster)) this.handleHit(monster);
    });
    this.shieldPool?.forEachActive((shield) => {
      if (this.physics.overlap(elephant, shield)) this.handleShieldCollect(shield);
    });
  }

  private handleCollect(nutrient: Nutrient): void {
    if (!this.elephant || !this.nutrientPool) return;
    const key = nutrient.getKey();
    if (!key) {
      this.nutrientPool.release(nutrient);
      return;
    }
    const value = nutrient.getValue();
    const color = NUTRIENTS[key].color;
    const position = { x: nutrient.x, y: nutrient.y };
    const multiplier = this.elephant.getScoreMultiplier();
    const points = this.scoreSystem.recordCollect(value, multiplier);
    this.scoreSystem.recordStage(this.elephant.getStage());
    this.elephant.addProgress(value);
    this.elephant.playCollectPunch();

    const payload: NutrientCollectedPayload = {
      key,
      value,
      isDielac: isDielacNutrient(key),
      position,
    };
    this.events.emit(GAME_EVENTS.NutrientCollected, payload);
    this.events.emit(GAME_EVENTS.ScoreChanged, this.scoreSystem.snapshot().total);
    this.events.emit(GAME_EVENTS.ProgressChanged, this.elephant.getProgress());

    emitBurst(this, position.x, position.y, color, { count: 7, lifeMs: 380 });
    this.playFloatScore(position.x, position.y, points);
    this.nutrientPool.release(nutrient);
  }

  private handleHit(monster: Monster): void {
    if (!this.elephant || !this.monsterPool || this.state !== 'playing') return;
    const position = { x: monster.x, y: monster.y };
    if (this.elephant.isInvincible()) {
      emitBurst(this, position.x, position.y, 0xffd84a, { count: 8, lifeMs: 350 });
      this.monsterPool.release(monster);
      return;
    }
    const result = this.elephant.takeHit();
    this.cameras.main.shake(220, 0.012);
    emitBurst(this, position.x, position.y, 0xff5050, { count: 10, lifeMs: 450, maxSpeed: 160 });
    this.monsterPool.release(monster);
    const payload: MonsterHitPayload = { position };
    this.events.emit(GAME_EVENTS.MonsterHit, payload);
    if (result.gameOver) this.endGame('tiny-hit');
  }

  private handleShieldCollect(shield: Shield): void {
    if (!this.elephant || !this.shieldPool) return;
    const position = { x: shield.x, y: shield.y };
    this.elephant.setInvincible(true);
    this.shieldExpireMs = this.elapsedMs + BALANCE.elephant.shieldDurationMs;
    this.shieldWarning = false;
    this.showShieldRing();
    this.shieldPool.release(shield);
    emitBurst(this, position.x, position.y, 0xffd84a, { count: 12, lifeMs: 500, maxSpeed: 150 });
    this.events.emit(GAME_EVENTS.ShieldActivated, BALANCE.elephant.shieldDurationMs);
  }

  private scheduleNextShield(): void {
    if (!this.rng) return;
    let minMs: number;
    let maxMs: number;
    if (!this.firstShieldScheduled) {
      minMs = BALANCE.spawn.firstShieldMinMs;
      maxMs = BALANCE.spawn.firstShieldMaxMs;
      this.firstShieldScheduled = true;
    } else {
      minMs = BALANCE.spawn.shieldIntervalMinMs;
      maxMs = BALANCE.spawn.shieldIntervalMaxMs;
    }
    this.shieldNextSpawnMs = this.elapsedMs + this.rng.range(minMs, maxMs);
  }

  private tickShieldSpawn(): void {
    if (!this.rng || !this.shieldPool) return;
    if (this.elapsedMs < this.shieldNextSpawnMs) return;
    const phase = this.getCurrentPhase();
    if (!phase) return;
    const lane = this.rng.rangeInt(0, BALANCE.lanes.count - 1) as Lane;
    const shield = this.shieldPool.acquire();
    shield.spawn(lane, phase.scrollSpeed);
    shield.setDepth(10);
    this.scheduleNextShield();
  }

  private tickShieldExpiry(): void {
    if (!this.elephant?.isInvincible()) return;
    if (this.elapsedMs >= this.shieldExpireMs) {
      this.elephant.setInvincible(false);
      this.hideShieldRing();
      this.events.emit(GAME_EVENTS.ShieldExpired);
      return;
    }
    if (this.shieldRing && this.elephant) {
      this.shieldRing.setPosition(this.elephant.x, this.elephant.y);
    }
    const remaining = this.shieldExpireMs - this.elapsedMs;
    if (remaining <= BALANCE.elephant.shieldWarnThresholdMs && !this.shieldWarning) {
      this.applyShieldWarnPulse();
    }
  }

  private showShieldRing(): void {
    if (!this.elephant) return;
    if (!this.shieldRing) {
      this.shieldRing = this.add
        .arc(this.elephant.x, this.elephant.y, 52, 0, 360, false, 0xffd84a, 0)
        .setStrokeStyle(3, 0xffd84a, 0.95)
        .setDepth(15);
    }
    this.shieldRing.setVisible(true).setAlpha(0.6).setScale(1);
    this.shieldPulseTween?.stop();
    this.shieldPulseTween = this.tweens.add({
      targets: this.shieldRing,
      alpha: { from: 0.4, to: 1 },
      scale: { from: 0.95, to: 1.1 },
      duration: 450,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private applyShieldWarnPulse(): void {
    if (!this.shieldRing) return;
    this.shieldWarning = true;
    this.shieldPulseTween?.stop();
    this.shieldPulseTween = this.tweens.add({
      targets: this.shieldRing,
      alpha: { from: 0.3, to: 1 },
      scale: { from: 0.9, to: 1.15 },
      duration: 110,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private hideShieldRing(): void {
    this.shieldPulseTween?.stop();
    this.shieldPulseTween = undefined;
    this.shieldWarning = false;
    this.shieldRing?.setVisible(false).setAlpha(1).setScale(1);
  }

  private onStageChanged(payload: StageChangedPayload): void {
    this.scoreSystem.recordStage(payload.stage);
    if (payload.reason === 'levelup') this.showLevelUpText();
  }

  private showLevelUpText(): void {
    const text = this.add
      .text(GAME_WIDTH / 2, BALANCE.game.elephantY - 90, 'LEVEL UP!', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '32px',
        fontStyle: 'bold',
        color: '#ffeb3b',
        stroke: '#1a1a1a',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(60)
      .setScale(0.3);
    this.tweens.add({
      targets: text,
      scale: 1.1,
      duration: 250,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: text,
      alpha: 0,
      y: text.y - 30,
      delay: 500,
      duration: 350,
      ease: 'Quad.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  private playFloatScore(x: number, y: number, points: number): void {
    const floatText = this.add
      .text(x, y, `+${points}`, {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#3b9f4a',
        stroke: '#ffffff',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(40)
      .setScale(0.6);
    this.tweens.add({
      targets: floatText,
      scale: 1.15,
      duration: 130,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: floatText,
      y: y - 38,
      alpha: 0,
      delay: 120,
      duration: 520,
      ease: 'Quad.easeOut',
      onComplete: () => floatText.destroy(),
    });
  }

  private startCountdown(): void {
    this.state = 'countdown';
    this.countdownText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '3', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '96px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setShadow(0, 4, '#0008', 8, true, true)
      .setDepth(60);

    const beats = ['3', '2', '1', 'GO!'];
    let i = 0;
    const showBeat = () => {
      const label = beats[i];
      if (label === undefined) return;
      this.countdownText?.setText(label).setAlpha(1).setScale(0.6);
      this.events.emit(GAME_EVENTS.CountdownTick, label);
      this.tweens.add({
        targets: this.countdownText,
        scale: 1.2,
        alpha: 0,
        duration: 800,
        ease: 'Quad.easeOut',
      });
    };
    showBeat();
    this.time.addEvent({
      delay: 1000,
      repeat: beats.length - 1,
      callback: () => {
        i++;
        if (i < beats.length) {
          showBeat();
        } else {
          this.countdownText?.destroy();
          this.countdownText = undefined;
          this.startGameplay();
        }
      },
    });
  }

  private startGameplay(): void {
    this.state = 'playing';
    this.elapsedMs = 0;
    this.spawnAccumulator = 0;
    this.scheduleNextShield();
    this.inputSystem?.setEnabled(true);
    this.events.emit(GAME_EVENTS.GameStarted);
  }

  private endGame(reason: GameOverPayload['reason']): void {
    if (this.state === 'over') return;
    this.state = 'over';
    this.inputSystem?.setEnabled(false);
    this.scoreSystem.finalize(reason === 'timer');
    const snap = this.scoreSystem.snapshot();
    const payload: GameOverPayload = {
      score: snap.total,
      maxStage: snap.maxStage,
      durationMs: this.elapsedMs,
      reason,
    };
    this.events.emit(GAME_EVENTS.GameOver, payload);
    this.playGameOverTransition(payload);
  }

  private playGameOverTransition(payload: GameOverPayload): void {
    const slowMoMs = BALANCE.game.slowMoBeforeGameOverMs;
    const overlay = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0)
      .setOrigin(0)
      .setDepth(200);
    this.tweens.add({
      targets: overlay,
      alpha: 0.5,
      duration: slowMoMs,
      ease: 'Quad.easeIn',
    });
    this.time.delayedCall(slowMoMs, () => this.scene.start(SCENES.GameOver, payload));
  }

  private bindBlurFocus(): void {
    const onBlur = () => this.pauseForBlur();
    const onFocus = () => this.showResumeOverlay();
    this.game.events.on(Phaser.Core.Events.BLUR, onBlur);
    this.game.events.on(Phaser.Core.Events.FOCUS, onFocus);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off(Phaser.Core.Events.BLUR, onBlur);
      this.game.events.off(Phaser.Core.Events.FOCUS, onFocus);
    });
  }

  private pauseForBlur(): void {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.inputSystem?.setEnabled(false);
    this.events.emit(GAME_EVENTS.GamePaused);
    this.showResumeOverlay();
  }

  private showResumeOverlay(): void {
    if (this.state !== 'paused' || this.pauseLayer) return;
    const layer = this.add.container(0, 0).setDepth(80);
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.55);
    bg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    layer.add(bg);
    const label = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Đã tạm dừng\nChạm để tiếp tục', {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#ffffff',
        align: 'center',
        lineSpacing: 6,
      })
      .setOrigin(0.5);
    layer.add(label);
    const hit = this.add
      .zone(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    layer.add(hit);
    hit.once('pointerup', () => this.resumeFromBlur());
    this.pauseLayer = layer;
  }

  private resumeFromBlur(): void {
    this.pauseLayer?.destroy();
    this.pauseLayer = undefined;
    this.state = 'playing';
    this.inputSystem?.setEnabled(true);
    this.events.emit(GAME_EVENTS.GameResumed);
  }

  private cleanup(): void {
    this.pauseLayer?.destroy();
    this.countdownText?.destroy();
    this.bgTile = undefined;
    this.elephant = undefined;
    this.inputSystem = undefined;
    this.nutrientPool = undefined;
    this.monsterPool = undefined;
    this.shieldPool = undefined;
    this.spawnSystem = undefined;
    this.rng = undefined;
    this.countdownText = undefined;
    this.shieldRing = undefined;
    this.shieldPulseTween = undefined;
    this.pauseLayer = undefined;
    this.shieldExpireMs = 0;
    this.shieldNextSpawnMs = 0;
    this.shieldWarning = false;
    this.firstShieldScheduled = false;
    this.elapsedMs = 0;
    this.spawnAccumulator = 0;
  }
}
